import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  coreExtensionData,
  createFrontendModule,
  useApi,
} from '@backstage/frontend-plugin-api';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import {
  scaffolderApiRef,
  type ScaffolderTask,
} from '@backstage/plugin-scaffolder-react';
import { ProjectCreationTaskPage } from './ProjectCreationTaskPage';
import { EnvironmentTaskPage } from './EnvironmentTaskPage';
import { ProjectDeletionTaskPage } from './ProjectDeletionTaskPage';
import { TaskProjectBanner } from './TaskProjectBanner';
import { loadTask } from './taskProject';
import {
  roleOfTemplate,
  useAppTypeDescriptors,
  type TemplateRole,
} from '../shared/templateRoles';

/**
 * Which page a task gets, by the role of its template (`roleOfTemplate`:
 * the embedded Odoo descriptor's and the generic refs, and the refs the
 * AppTypes' descriptors designate): ours for a project's creation and deletion and the five
 * environment roles (wake, sleep, promotion, configuration, point-in-time
 * restore), else upstream's.
 */
type TaskPageChoice =
  | 'loading'
  | 'deletion'
  | 'creation'
  | 'environment'
  | 'default';

const PAGES: Record<TemplateRole, TaskPageChoice> = {
  delete: 'deletion',
  create: 'creation',
  wake: 'environment',
  sleep: 'environment',
  promote: 'environment',
  configure: 'environment',
  // The point-in-time restore, its validation and its cancellation: their
  // steps by the task's `operation` (`pageOf`, `environmentTask.ts`).
  restore: 'environment',
};

/**
 * The page for one task: the project deletion's own page
 * (`ProjectDeletionTaskPage`) for a deletion template, the project
 * creation's (`ProjectCreationTaskPage`) for a creation template,
 * the environment one (`EnvironmentTaskPage`) for a wake, a sleep or a
 * promotion,
 * the original sub-page for any other template and for a task that cannot
 * be read (it reports that itself). Nothing while the task loads — and, for
 * a template whose role its name does not say, while the descriptors load
 * (at most `DESCRIPTORS_WAIT_MS`) —, so no page shows then switches. The
 * banner above the deletion and the original pages; the creation page shows
 * its own, which turns into the result once the task ends.
 */
function TaskPage(props: { taskId: string; original: ReactNode }) {
  const { taskId, original } = props;
  const scaffolderApi = useApi(scaffolderApiRef);
  const [task, setTask] = useState<ScaffolderTask | 'failed'>();

  useEffect(() => {
    let active = true;
    loadTask(scaffolderApi, taskId).then(
      found => {
        if (active) setTask(found);
      },
      () => {
        if (active) setTask('failed');
      },
    );
    return () => {
      active = false;
    };
  }, [scaffolderApi, taskId]);

  const ref =
    task && task !== 'failed' ? task.spec.templateInfo?.entityRef : undefined;
  const staticRole = roleOfTemplate(ref);
  const descriptors = useAppTypeDescriptors({
    enabled: ref !== undefined && staticRole === undefined,
  });

  let choice: TaskPageChoice = 'default';
  if (task === undefined) {
    choice = 'loading';
  } else if (staticRole) {
    choice = PAGES[staticRole];
  } else if (descriptors.status === 'loading') {
    choice = 'loading';
  } else if (descriptors.status === 'ready') {
    const role = roleOfTemplate(ref, descriptors.items);
    choice = role ? PAGES[role] : 'default';
  }

  return (
    <>
      {choice !== 'creation' && <TaskProjectBanner taskId={taskId} />}
      {choice === 'deletion' && <ProjectDeletionTaskPage taskId={taskId} />}
      {choice === 'creation' && <ProjectCreationTaskPage taskId={taskId} />}
      {choice === 'environment' && <EnvironmentTaskPage taskId={taskId} />}
      {choice === 'default' && original}
    </>
  );
}

/**
 * The sub-page's content: on a task (`/create/tasks/:taskId`), `TaskPage`;
 * on the task list, the original element untouched.
 *
 * The page routes its sub-pages as `tasks/*` (PageBlueprint), so the task id
 * is the splat parameter. The original element (lazy, in its own
 * ExtensionBoundary) routes `:taskId` itself, relative to that same
 * `tasks/*`: it must be rendered here, outside any nested `<Route>` of ours,
 * or its own routes would only see what such a route leaves of the path.
 */
function TasksSubPage(props: { original: ReactNode }) {
  const rest = useParams()['*'] ?? '';
  const taskId = /^[^/]+$/.test(rest) ? rest : undefined;
  return (
    // `.waadoo-task-page` (styles.css): the hook for the taller logs and the
    // report's tables.
    <div className="waadoo-task-page">
      {taskId ? (
        <TaskPage key={taskId} taskId={taskId} original={props.original} />
      ) : (
        props.original
      )}
    </div>
  );
}

/**
 * The scaffolder's « Tasks » sub-page (`sub-page:scaffolder/tasks`), kept as
 * the plugin builds it — path, title, tab, its `templateOutputsComponents`
 * input — with only its element wrapped (`TasksSubPage`): the project banner
 * on a task, and for a task of the deletion or the creation template its own
 * page in place of the original one.
 */
const tasksSubPageWithProjectBanner = scaffolderPlugin
  .getExtension('sub-page:scaffolder/tasks')
  .override({
    factory(originalFactory) {
      const original = originalFactory();
      const element = original.get(coreExtensionData.reactElement);
      return [
        ...Array.from(original).filter(
          value => value.id !== coreExtensionData.reactElement.id,
        ),
        coreExtensionData.reactElement(<TasksSubPage original={element} />),
      ];
    },
  });

/**
 * On the `scaffolder` plugin: an extension of a module replaces the plugin's
 * extension with the same id. Registered in packages/app/src/App.tsx.
 */
export const taskProjectBannerModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [tasksSubPageWithProjectBanner],
});
